/**
 * Paid-media attribution for the AppLovin campaign (and any UTM-tagged link).
 *
 * The campaign page (discover.globalyoungscholar.com) sends parents to /students/register with AppLovin's click id
 * (`aleid`), `alart` and UTM tags in the query string. The register flow moves between steps with router state, so
 * the query string is lost after the first step. `captureAttribution()` runs once on app load, keeps the values in
 * localStorage for 30 days, and `attributionForOrder()` hands them to the order request so the backend can report
 * the paid order to AppLovin's Conversions API. No personal data is stored or sent.
 */
const STORAGE_KEY = 'gys_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const PARAMS = [
  'aleid',
  'alart',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'plan',
  'cta',
] as const;

type Param = (typeof PARAMS)[number];
export type Attribution = Partial<Record<Param, string>> & {
  captured_at?: number;
  landing_path?: string;
};

function readCookie(name: string): string | undefined {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
}

export function getAttribution(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Attribution;
    if (!a.captured_at || Date.now() - a.captured_at > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

/** Call once, before the app renders. Safe to call on every load; it only writes when it finds something. */
export function captureAttribution(): void {
  try {
    const q = new URLSearchParams(window.location.search);
    const found: Attribution = {};
    PARAMS.forEach((k) => {
      const v = q.get(k);
      if (v) found[k] = v.slice(0, 200);
    });
    // Backup: the campaign page also writes aleid to a cookie shared across *.globalyoungscholar.com.
    if (!found.aleid) {
      const fromCookie = readCookie('gys_aleid');
      if (fromCookie) found.aleid = fromCookie.slice(0, 200);
    }
    if (!Object.keys(found).length) return;
    const prev = getAttribution() ?? {};
    // A new click id starts a fresh attribution; otherwise new values are merged over the old ones.
    const next: Attribution =
      found.aleid && found.aleid !== prev.aleid ? { ...found } : { ...prev, ...found };
    next.captured_at = Date.now();
    next.landing_path = window.location.pathname;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage blocked (private mode etc.): attribution is best-effort
  }
}

/**
 * True when the visitor arrived from a Discovery ad (?plan=discovery on the sign-up link). These visitors get the
 * short checkout: one details screen (any email, school name typed in), then straight to payment for Discovery.
 */
export function isDiscoveryAdFlow(): boolean {
  return getAttribution()?.plan === 'discovery';
}

/** The object sent with the registration order, or undefined when there is nothing to send. */
export function attributionForOrder(): Attribution | undefined {
  const a = getAttribution();
  return a && (a.aleid || a.utm_source) ? a : undefined;
}
